import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Icoon } from '../ui/Icoon';
import { useState } from 'react';
import { MODULES, MODULE_LABEL, ROL_LABEL, type Licentie, type Module, type Rol, type Wijziging } from '@ep/core';
import { WijzigingenLijst } from '../ui/Wijzigingen';
import { api } from '../lib/api';
import { toast } from '../ui/toast';
import { Badge, Laden, Modal, Veld } from '../ui/ui';

interface Org {
  id: string;
  naam: string;
  slug: string;
  plan: string;
  trialTot: string | null;
  actief: boolean;
  graphConsent: boolean;
  entraTenantId: string | null;
  maxEngineers: number;
  aangemaakt: string;
  gebruikers: number;
  engineers: number;
  projecten: number;
  taken: number;
  laatstActief: string | null;
  demo: boolean;
  abonnement: 'TRIAL' | 'STRIPE' | 'HANDMATIG';
  modules: string[];
  abonnementStatus: string | null;
  betaalwijze: string | null;
  betaaldTot: string | null;
  opzeggingPer: string | null;
  stripeKlantId: string | null;
  licentie: Licentie;
  /** SPEC §16.3 */
  bewaarTot: string | null;
  verwijderOp: string | null;
}
const SOORT: Record<Org['abonnement'], string> = { TRIAL: 'Proef', STRIPE: 'Stripe', HANDMATIG: 'Handmatig' };
const TOEGANG_TOON = { VOL: 'groen', MELDING: 'oranje', GEBLOKKEERD: 'rood' } as const;
interface Gebruiker { id: string; naam: string; email: string; rol: Rol; laatstGezien: string | null }

const PLANNEN = ['TRIAL', 'STARTER', 'PRO', 'ENTERPRISE'];
const PLAN_LABEL: Record<string, string> = { TRIAL: 'Proef', STARTER: 'Starter (15)', PRO: 'Pro (75)', ENTERPRISE: 'Enterprise' };
const datum = (d: string | null) => (d ? new Date(d).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }) : '–');

/** Platformbeheer: eigen omgeving voor de leverancier, los van de organisaties. Adres: /platform */
export function Platform() {
  const ik = useQuery({ queryKey: ['platform-ik'], queryFn: () => api.get<{ ingelogd: boolean; email: string | null; ingesteld: boolean }>('/platform-api/ik') });
  if (!ik.data) return <Laden />;
  if (!ik.data.ingelogd) return <PlatformLogin ingesteld={ik.data.ingesteld} />;
  return <PlatformDashboard email={ik.data.email!} />;
}

function PlatformLogin({ ingesteld }: { ingesteld: boolean }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [ww, setWw] = useState('');
  const login = async () => {
    try {
      await api.post('/platform-api/login', { email, wachtwoord: ww });
      await qc.invalidateQueries({ queryKey: ['platform-ik'] });
    } catch (e) {
      toast((e as Error).message, 'fout');
    }
  };
  return (
    <div className="midden" style={{ background: 'var(--ink)' }}>
      <div className="paneel login kolom">
        <div className="logo">
          <img src="/icon-192.png" width={40} height={40} alt="" style={{ borderRadius: 9 }} />
          <div>
            <h1>Platformbeheer</h1>
            <div className="mute klein">bsmartr · alleen voor de leverancier</div>
          </div>
        </div>
        {!ingesteld && <div className="paneel vlak" style={{ background: 'var(--oranje-bg)' }}>Nog niet ingesteld: zet <b>PLATFORM_ADMIN_EMAIL</b> en <b>PLATFORM_ADMIN_WACHTWOORD</b> bij de stack-variabelen.</div>}
        <Veld label="E-mailadres"><input value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" /></Veld>
        <Veld label="Wachtwoord"><input type="password" value={ww} onChange={(e) => setWw(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && login()} autoComplete="current-password" /></Veld>
        <button className="knop primair" onClick={login} disabled={!ingesteld}>Inloggen</button>
      </div>
    </div>
  );
}

function PlatformDashboard({ email }: { email: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['platform'], queryFn: () => api.get<Org[]>('/platform-api/organisaties') });
  const [nieuw, setNieuw] = useState(false);
  const [open, setOpen] = useState<Org | null>(null);
  const [zoek, setZoek] = useState('');
  const ververs = () => qc.invalidateQueries({ queryKey: ['platform'] });
  const wijzig = async (id: string, body: object) => {
    try {
      await api.patch(`/platform-api/organisaties/${id}`, body);
      await ververs();
      toast('Opgeslagen', 'succes');
    } catch (e) {
      toast((e as Error).message, 'fout');
    }
  };
  const demo = async () => {
    try {
      const r = await api.post<{ inlog: string[] }>('/platform-api/demo', { naam: 'Demo Zorg-ICT B.V.' });
      await ververs();
      toast(`Demo-organisatie aangemaakt. Inloggen met: ${r.inlog.join(', ')}`, 'succes');
    } catch (e) {
      toast((e as Error).message, 'fout');
    }
  };
  const uitloggen = async () => {
    await api.post('/platform-api/uitloggen');
    await qc.invalidateQueries({ queryKey: ['platform-ik'] });
  };
  const orgs = (q.data ?? []).filter((o) => !zoek || o.naam.toLowerCase().includes(zoek.toLowerCase()));
  const tot = q.data ?? [];
  return (
    <>
      <header className="kop" style={{ background: 'var(--ink)' }}>
        <div className="merk">
          <img src="/logo-wit.svg" alt="bsmartr" />
          <span className="scheiding" />
          <div>
            <div className="titel">Platformbeheer</div>
            <div className="sub">ingelogd als {email}</div>
          </div>
        </div>
        <nav>
          <a href="/" target="_blank" rel="noreferrer">App openen ↗</a>
          <button onClick={uitloggen}>Uitloggen</button>
        </nav>
      </header>
      <div className="pagina kolom" style={{ gap: 16 }}>
        <div className="kaartjes">
          <div className="kaartje"><div className="getal">{tot.length}</div><div className="lbl">organisaties</div></div>
          <div className="kaartje"><div className="getal">{tot.filter((o) => o.actief).length}</div><div className="lbl">actief</div></div>
          <div className="kaartje"><div className="getal">{tot.filter((o) => o.abonnement === 'TRIAL').length}</div><div className="lbl">in proefperiode</div></div>
          <div className="kaartje"><div className="getal">{tot.filter((o) => o.abonnement === 'STRIPE' && o.licentie.toegang !== 'GEBLOKKEERD').length}</div><div className="lbl">betalend via Stripe</div></div>
          <div className="kaartje"><div className="getal">{tot.reduce((s, o) => s + o.engineers, 0)}</div><div className="lbl">engineers totaal</div></div>
          <div className="kaartje"><div className="getal">{tot.reduce((s, o) => s + o.gebruikers, 0)}</div><div className="lbl">gebruikers totaal</div></div>
        </div>
        <div className="paneel">
          <div className="sectie-kop">
            <h1 className="groei">Organisaties</h1>
            <input value={zoek} onChange={(e) => setZoek(e.target.value)} placeholder="Zoeken…" style={{ width: 220 }} />
            <button className="knop" onClick={demo}>+ Demo-organisatie</button>
            <button className="knop primair" onClick={() => setNieuw(true)}>+ Nieuwe organisatie</button>
          </div>
          {!q.data ? (
            <Laden />
          ) : (
            <table className="tabel">
              <thead>
                <tr><th>Organisatie</th><th>Plan</th><th>Licentie</th><th>Engineers</th><th>Gebruikers</th><th>Projecten / taken</th><th>Outlook</th><th>Laatst actief</th><th>Status</th></tr>
              </thead>
              <tbody>
                {orgs.map((o) => (
                  <tr key={o.id} className="klik" onClick={() => setOpen(o)}>
                    <td><b>{o.naam}</b><div className="klein mute">{o.slug} · sinds {datum(o.aangemaakt)}</div></td>
                    <td>{PLAN_LABEL[o.plan]}{o.abonnement === 'TRIAL' && o.trialTot && <div className="klein mute">tot {datum(o.trialTot)}</div>}</td>
                    <td>
                      <Badge toon={TOEGANG_TOON[o.licentie.toegang]} title={o.licentie.melding ?? undefined}>{SOORT[o.abonnement]}{o.abonnement === 'STRIPE' && o.abonnementStatus ? ` · ${o.abonnementStatus}` : ''}</Badge>
                      <div className="klein mute">{o.licentie.modules.map((m) => MODULE_LABEL[m]).join(', ') || 'basis'}</div>
                      {o.verwijderOp && <div className="klein" style={{ color: 'var(--rood)' }}>wordt verwijderd op {datum(o.verwijderOp)}</div>}
                    </td>
                    <td>{o.engineers} / {o.maxEngineers}</td>
                    <td>{o.gebruikers}</td>
                    <td>{o.projecten} / {o.taken}</td>
                    <td>{o.graphConsent ? <Badge toon="groen">gekoppeld</Badge> : <Badge toon="grijs">test</Badge>}</td>
                    <td className="klein">{datum(o.laatstActief)}</td>
                    <td>{o.actief ? <Badge toon="groen">actief</Badge> : <Badge toon="rood">uit</Badge>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {q.data && !orgs.length && <div className="leeg">Nog geen organisaties.</div>}
        </div>
        <Prijzen />
        <Versies />
      </div>
      {nieuw && <NieuweOrganisatie onSluit={() => setNieuw(false)} onKlaar={ververs} />}
      {open && <OrganisatieDetail org={open} onSluit={() => setOpen(null)} wijzig={wijzig} onVerwijderd={() => { setOpen(null); ververs(); }} />}
    </>
  );
}

function NieuweOrganisatie({ onSluit, onKlaar }: { onSluit: () => void; onKlaar: () => void }) {
  const [f, setF] = useState({ naam: '', plan: 'TRIAL', beheerderNaam: '', beheerderEmail: '', entraTenantId: '' });
  const opslaan = async () => {
    try {
      await api.post('/platform-api/organisaties', { ...f, entraTenantId: f.entraTenantId || null });
      toast(`Organisatie ${f.naam} aangemaakt. ${f.beheerderEmail} kan nu inloggen als beheerder.`, 'succes');
      onKlaar();
      onSluit();
    } catch (e) {
      toast((e as Error).message, 'fout');
    }
  };
  return (
    <Modal titel="Nieuwe organisatie" onSluit={onSluit} voet={<><button className="knop" onClick={onSluit}>Annuleren</button><button className="knop primair" onClick={opslaan}>Aanmaken</button></>}>
      <div className="grid2">
        <Veld label="Naam organisatie *" className="span2"><input value={f.naam} onChange={(e) => setF({ ...f, naam: e.target.value })} /></Veld>
        <Veld label="Abonnement">
          <select value={f.plan} onChange={(e) => setF({ ...f, plan: e.target.value })}>
            {PLANNEN.map((p) => <option key={p} value={p}>{PLAN_LABEL[p]}</option>)}
          </select>
        </Veld>
        <Veld label="Microsoft tenant-ID (optioneel)" hint="Leeg laten: wordt gekoppeld bij de eerste Microsoft-login van de beheerder">
          <input value={f.entraTenantId} onChange={(e) => setF({ ...f, entraTenantId: e.target.value })} placeholder="00000000-0000-…" />
        </Veld>
        <Veld label="Naam eerste beheerder *"><input value={f.beheerderNaam} onChange={(e) => setF({ ...f, beheerderNaam: e.target.value })} /></Veld>
        <Veld label="E-mail eerste beheerder *"><input value={f.beheerderEmail} onChange={(e) => setF({ ...f, beheerderEmail: e.target.value })} /></Veld>
      </div>
      <p className="klein mute">De beheerder logt in met dit e-mailadres en voegt daarna zelf engineers, expertises en collega's toe.</p>
    </Modal>
  );
}

function OrganisatieDetail({ org, onSluit, wijzig, onVerwijderd }: { org: Org; onSluit: () => void; wijzig: (id: string, b: object) => Promise<void>; onVerwijderd: () => void }) {
  const qc = useQueryClient();
  const g = useQuery({ queryKey: ['platform-gebr', org.id], queryFn: () => api.get<Gebruiker[]>(`/platform-api/organisaties/${org.id}/gebruikers`) });
  const [f, setF] = useState({ naam: org.naam, plan: org.plan, maxEngineers: String(org.maxEngineers), trialTot: org.trialTot ?? '', bewaarTot: org.bewaarTot ?? '', actief: org.actief, entraTenantId: org.entraTenantId ?? '', abonnement: org.abonnement, modules: org.modules as Module[] });
  const [nb, setNb] = useState({ naam: '', email: '', rol: 'BEHEERDER' });
  const [bevestig, setBevestig] = useState('');
  const fout = (e: unknown) => toast((e as Error).message, 'fout');
  const bewaar = () => wijzig(org.id, { naam: f.naam, plan: f.plan, maxEngineers: Number(f.maxEngineers), trialTot: f.trialTot || null, bewaarTot: f.bewaarTot || null, actief: f.actief, entraTenantId: f.entraTenantId || null, abonnement: f.abonnement, modules: f.modules });
  const voegToe = async () => {
    try {
      await api.post(`/platform-api/organisaties/${org.id}/gebruikers`, nb);
      setNb({ naam: '', email: '', rol: 'BEHEERDER' });
      await qc.invalidateQueries({ queryKey: ['platform-gebr', org.id] });
      toast('Gebruiker toegevoegd', 'succes');
    } catch (e) {
      fout(e);
    }
  };
  const haalWeg = async (uid: string) => {
    try {
      await api.del(`/platform-api/organisaties/${org.id}/gebruikers/${uid}`);
      await qc.invalidateQueries({ queryKey: ['platform-gebr', org.id] });
    } catch (e) {
      fout(e);
    }
  };
  const meekijken = async () => {
    try {
      await api.post(`/platform-api/organisaties/${org.id}/meekijken`);
      window.open('/', '_blank');
    } catch (e) {
      fout(e);
    }
  };
  const verwijder = async () => {
    try {
      await fetch(`/platform-api/organisaties/${org.id}`, { method: 'DELETE', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ bevestigNaam: bevestig }) }).then(async (r) => {
        if (!r.ok) throw new Error((await r.json()).fout);
      });
      toast(`${org.naam} is verwijderd`, 'succes');
      onVerwijderd();
    } catch (e) {
      fout(e);
    }
  };
  const [opnieuw, setOpnieuw] = useState(false);
  const demoOpnieuw = async () => {
    try {
      const r = await api.post<{ inlog: string[]; behouden: number }>(`/platform-api/organisaties/${org.id}/demo-opnieuw`);
      toast(`Demo opnieuw opgebouwd${r.behouden ? ` (${r.behouden} eigen gebruiker(s) behouden)` : ''}. Demo-inlog: ${r.inlog.join(', ')}`, 'succes');
      onVerwijderd();
    } catch (e) {
      fout(e);
    }
  };
  return (
    <Modal titel={org.naam} onSluit={onSluit} breedte="breed" extraKop={<button className="knop" onClick={meekijken} title="Opent de app in een nieuw tabblad als beheerder van deze organisatie"><Icoon naam="eye" maat={16} /> Meekijken als beheerder</button>}>
      <div className="grid3">
        <Veld label="Naam"><input value={f.naam} onChange={(e) => setF({ ...f, naam: e.target.value })} /></Veld>
        <Veld label="Plan">
          <select value={f.plan} onChange={(e) => setF({ ...f, plan: e.target.value })}>
            {PLANNEN.map((p) => <option key={p} value={p}>{PLAN_LABEL[p]}</option>)}
          </select>
        </Veld>
        <Veld label="Soort licentie" hint={f.abonnement === 'STRIPE' ? 'Plan en modules volgen Stripe' : f.abonnement === 'TRIAL' ? 'Volledig tot de proefdatum' : 'Contract buiten Stripe; loopt niet af'}>
          <select value={f.abonnement} onChange={(e) => setF({ ...f, abonnement: e.target.value as Org['abonnement'] })}>
            <option value="TRIAL">Proefperiode</option>
            <option value="HANDMATIG">Handmatig (contract)</option>
            {org.abonnement === 'STRIPE' && <option value="STRIPE">Stripe</option>}
          </select>
        </Veld>
        <Veld label="Modules" hint={f.plan === 'ENTERPRISE' || f.abonnement === 'TRIAL' ? 'Bij Enterprise en in de proef staan alle modules aan' : undefined}>
          <div className="rij" style={{ gap: 12, minHeight: 34 }}>
            {MODULES.map((m) => (
              <label key={m} className="check">
                <input type="checkbox" checked={f.modules.includes(m)} onChange={(e) => setF({ ...f, modules: e.target.checked ? [...f.modules, m] : f.modules.filter((x) => x !== m) })} /> {MODULE_LABEL[m]}
              </label>
            ))}
          </div>
        </Veld>
        <Veld label="Max. actieve engineers"><input value={f.maxEngineers} onChange={(e) => setF({ ...f, maxEngineers: e.target.value })} /></Veld>
        <Veld label="Proefperiode tot"><input type="date" value={f.trialTot} onChange={(e) => setF({ ...f, trialTot: e.target.value })} /></Veld>
        <Veld label="Bewaren tot" hint={org.verwijderOp ? `Wordt nu verwijderd op ${datum(org.verwijderOp)} (3 maanden na het einde). Een latere datum stelt dat uit.` : 'Alleen nodig om automatisch verwijderen na een beëindigd abonnement uit te stellen.'}>
          <input type="date" value={f.bewaarTot} onChange={(e) => setF({ ...f, bewaarTot: e.target.value })} />
        </Veld>
        <Veld label="Microsoft tenant-ID"><input value={f.entraTenantId} onChange={(e) => setF({ ...f, entraTenantId: e.target.value })} /></Veld>
        <label className="check" style={{ alignSelf: 'end' }}><input type="checkbox" checked={f.actief} onChange={(e) => setF({ ...f, actief: e.target.checked })} /> Actief (uit = niemand kan inloggen)</label>
      </div>
      <div className="rij" style={{ marginTop: 10 }}>
        <span className="groei klein">
          <Badge toon={TOEGANG_TOON[org.licentie.toegang]}>{org.licentie.toegang === 'VOL' ? 'volledige toegang' : org.licentie.toegang === 'MELDING' ? 'toegang met melding' : 'geblokkeerd'}</Badge>{' '}
          {org.licentie.melding}
          {org.abonnement === 'STRIPE' && (
            <span className="mute">
              {' '}· {org.abonnementStatus} · {org.betaalwijze === 'FACTUUR' ? 'op factuur' : 'automatisch'} · betaald tot {datum(org.betaaldTot)}
              {org.opzeggingPer ? ` · opgezegd per ${datum(org.opzeggingPer)}` : ''}
            </span>
          )}
          {org.stripeKlantId && (
            <>
              {' '}· <a href={`https://dashboard.stripe.com/customers/${org.stripeKlantId}`} target="_blank" rel="noreferrer">klant in Stripe ↗</a>
            </>
          )}
        </span>
        <button className="knop primair" onClick={bewaar}>Opslaan</button>
      </div>
      {org.demo && (
        <div className="paneel vlak kolom" style={{ marginTop: 12, gap: 8, background: 'var(--merk-bg)' }}>
          <b>Demo-organisatie</b>
          <span className="klein">Bouw de demo opnieuw op met verse, verzonnen gegevens rond vandaag: engineers, klanten, projecten, een agile project met sprints en geschreven uren. Alles in deze organisatie wordt vervangen; gebruikers met een eigen (niet-demo) e-mailadres blijven.</span>
          <div className="rij">
            {opnieuw ? (
              <>
                <button className="knop merk" onClick={demoOpnieuw}>Ja, opnieuw opbouwen</button>
                <button className="knop" onClick={() => setOpnieuw(false)}>Annuleren</button>
              </>
            ) : (
              <button className="knop" onClick={() => setOpnieuw(true)}><Icoon naam="refresh-cw" maat={16} /> Demogegevens opnieuw opbouwen</button>
            )}
          </div>
        </div>
      )}

      <h3 style={{ margin: '16px 0 8px' }}>Gebruikers ({g.data?.length ?? 0})</h3>
      <table className="tabel">
        <thead><tr><th>Naam</th><th>E-mail</th><th>Rol</th><th>Laatst gezien</th><th /></tr></thead>
        <tbody>
          {(g.data ?? []).map((u) => (
            <tr key={u.id}>
              <td>{u.naam}</td>
              <td>{u.email}</td>
              <td>{ROL_LABEL[u.rol]}</td>
              <td className="klein">{datum(u.laatstGezien)}</td>
              <td><button className="icoon-knop" title="Verwijderen" onClick={() => haalWeg(u.id)}><Icoon naam="trash-2" maat={16} /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="rij" style={{ marginTop: 8 }}>
        <input placeholder="Naam" value={nb.naam} onChange={(e) => setNb({ ...nb, naam: e.target.value })} />
        <input placeholder="E-mail" value={nb.email} onChange={(e) => setNb({ ...nb, email: e.target.value })} />
        <select value={nb.rol} onChange={(e) => setNb({ ...nb, rol: e.target.value })} style={{ width: 180 }}>
          <option value="BEHEERDER">Beheerder</option>
          <option value="PLANNER">Planner</option>
          <option value="PROJECTMANAGER">Projectmanager</option>
        </select>
        <button className="knop" onClick={voegToe}>Toevoegen</button>
      </div>

      <div className="paneel vlak" style={{ marginTop: 20, borderColor: 'var(--rood)' }}>
        <b style={{ color: 'var(--rood)' }}>Organisatie verwijderen</b>
        <p className="klein mute">Verwijdert de organisatie met álle gegevens (projecten, taken, engineers, gebruikers). Dit kan niet ongedaan worden. Typ de naam <b>{org.naam}</b> om te bevestigen.</p>
        <div className="rij">
          <input value={bevestig} onChange={(e) => setBevestig(e.target.value)} placeholder={org.naam} />
          <button className="knop gevaar bevestig" disabled={bevestig.trim() !== org.naam} onClick={verwijder}>Definitief verwijderen</button>
        </div>
      </div>
    </Modal>
  );
}

/** SPEC §10 – versies en wijzigingslog, met de uitrol-historie uit de database. */
function Versies() {
  const q = useQuery({ queryKey: ['platform-versies'], queryFn: () => api.get<{ huidig: string; wijzigingen: Wijziging[]; uitrol: { id: string; versie: string; gestart: string }[] }>('/platform-api/versies') });
  if (!q.data) return null;
  // eerste keer dat een versie live ging
  const live = new Map<string, string>();
  for (const u of [...q.data.uitrol].reverse()) if (!live.has(u.versie)) live.set(u.versie, u.gestart);
  return (
    <div className="paneel kolom" style={{ gap: 12 }}>
      <div className="sectie-kop">
        <h1 className="groei">Versies en wijzigingen</h1>
        <span className="badge toon-oranje">nu live: v{q.data.huidig}</span>
      </div>
      {q.data.uitrol.length > 0 && (
        <div className="klein mute">
          Uitrol-log: {q.data.uitrol.slice(0, 10).map((u) => `v${u.versie} op ${new Date(u.gestart).toLocaleString('nl-NL', { dateStyle: 'medium', timeStyle: 'short' })}`).join(' · ')}
        </div>
      )}
      <WijzigingenLijst wijzigingen={q.data.wijzigingen} live={live} huidig={q.data.huidig} />
    </div>
  );
}

interface PrijsRij { sleutel: string; prijsId: string; omschrijving: string | null; perEngineer: boolean }
const PRIJS_LABEL: Record<string, string> = {
  'PLAN:STARTER': 'Plan Starter',
  'PLAN:PRO': 'Plan Pro',
  'PLAN:ENTERPRISE': 'Plan Enterprise',
  'MODULE:AGILE': 'Module Agile',
  'MODULE:UREN': 'Module Urenregistratie',
  BTW: 'Btw-tarief (optioneel)',
};

/** SPEC §12.4: Stripe-prijzen per onderdeel. */
function Prijzen() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['platform-prijzen'], queryFn: () => api.get<{ stripeAan: boolean; webhookAan: boolean; sleutels: string[]; prijzen: PrijsRij[] }>('/platform-api/prijzen') });
  const [f, setF] = useState<Record<string, PrijsRij>>({});
  if (!q.data) return null;
  const waarde = (k: string): PrijsRij => f[k] ?? q.data.prijzen.find((p) => p.sleutel === k) ?? { sleutel: k, prijsId: '', omschrijving: '', perEngineer: false };
  const zet = (k: string, w: Partial<PrijsRij>) => setF({ ...f, [k]: { ...waarde(k), ...w } });
  const bewaar = async (k: string) => {
    try {
      const w = waarde(k);
      await api.put('/platform-api/prijzen', { sleutel: k, prijsId: w.prijsId, omschrijving: w.omschrijving, perEngineer: w.perEngineer });
      await qc.invalidateQueries({ queryKey: ['platform-prijzen'] });
      const rest = { ...f };
      delete rest[k];
      setF(rest);
      toast(w.prijsId ? 'Prijs opgeslagen' : 'Prijs verwijderd', 'succes');
    } catch (e) {
      toast((e as Error).message, 'fout');
    }
  };
  return (
    <div className="paneel">
      <div className="sectie-kop">
        <h1 className="groei">Prijzen (Stripe)</h1>
        <Badge toon={q.data.stripeAan ? 'groen' : 'grijs'}>{q.data.stripeAan ? 'Stripe-sleutel ingesteld' : 'STRIPE_SECRET_KEY ontbreekt'}</Badge>
        <Badge toon={q.data.webhookAan ? 'groen' : 'grijs'}>{q.data.webhookAan ? 'webhook ingesteld' : 'STRIPE_WEBHOOK_SECRET ontbreekt'}</Badge>
      </div>
      <p className="klein mute" style={{ marginTop: 0 }}>
        Maak in Stripe per onderdeel een product met een terugkerende prijs en plak hier de prijs-id (<code>price_…</code>). De omschrijving ziet de klant bij Instellingen › Abonnement. <i>Per engineer</i>: het aantal volgt het aantal actieve engineers.
      </p>
      <table className="tabel">
        <thead><tr><th>Onderdeel</th><th>Stripe-id</th><th>Omschrijving voor de klant</th><th>Per engineer</th><th /></tr></thead>
        <tbody>
          {q.data.sleutels.map((k) => {
            const w = waarde(k);
            return (
              <tr key={k}>
                <td className="nowrap"><b>{PRIJS_LABEL[k] ?? k}</b></td>
                <td><input value={w.prijsId} onChange={(e) => zet(k, { prijsId: e.target.value })} placeholder={k === 'BTW' ? 'txr_…' : 'price_…'} style={{ width: 230 }} /></td>
                <td>{k !== 'BTW' && <input value={w.omschrijving ?? ''} onChange={(e) => zet(k, { omschrijving: e.target.value })} placeholder="bijv. € 12 per engineer per maand" style={{ width: '100%' }} />}</td>
                <td>{k !== 'BTW' && <input type="checkbox" checked={w.perEngineer} onChange={(e) => zet(k, { perEngineer: e.target.checked })} />}</td>
                <td><button className="knop klein" disabled={!f[k]} onClick={() => bewaar(k)}>Opslaan</button></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
